import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { rankingAPI } from '../../services/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Trophy, Crown, Medal, Gift, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const RankingManagement = () => {
  const [currentRanking, setCurrentRanking] = useState([]);
  const [winners, setWinners] = useState([]);
  const [claimsHistory, setClaimsHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [winnersFilter, setWinnersFilter] = useState({ start_date: '', end_date: '' });
  const [claimsFilter, setClaimsFilter] = useState({ start_date: '', end_date: '', kako_id: '' });
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustingUser, setAdjustingUser] = useState(null);
  const [pointsAdjustment, setPointsAdjustment] = useState('');
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveUserConfirm, setShowRemoveUserConfirm] = useState(false);
  const [removingUser, setRemovingUser] = useState(null); // {kakoId, date} // IDs selecionados para delete
  const [claimsSortBy, setClaimsSortBy] = useState('date_desc');

  const [prizes, setPrizes] = useState([
    { position: 1, description: '1º Lugar - Ouro', type: 'fixed', value: 100 },
    { position: 2, description: '2º Lugar - Prata', type: 'fixed', value: 50 },
    { position: 3, description: '3º Lugar - Bronze', type: 'fixed', value: 25 },
    { position: 4, description: '4º Lugar', type: 'fixed', value: 10 },
    { position: 5, description: '5º Lugar', type: 'fixed', value: 10 },
    { position: 6, description: '6º Lugar', type: 'fixed', value: 5 },
    { position: 7, description: '7º Lugar', type: 'fixed', value: 5 },
    { position: 8, description: '8º Lugar', type: 'fixed', value: 5 },
    { position: 9, description: '9º Lugar', type: 'fixed', value: 5 },
    { position: 10, description: '10º Lugar', type: 'fixed', value: 5 }
  ]);

  useEffect(() => {
    loadData();
    loadClaimsHistory();
  }, []);

  const loadData = async () => {
    try {
      const [rankingRes, winnersRes, configRes] = await Promise.all([
        rankingAPI.getCurrent(10),
        rankingAPI.getWinners(30),
        rankingAPI.getConfig()
      ]);
      setCurrentRanking(rankingRes.data);
      setWinners(winnersRes.data);
      setConfig(configRes.data);
      if (configRes.data.prizes && configRes.data.prizes.length > 0) {
        setPrizes(configRes.data.prizes);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadClaimsHistory = async () => {
    try {
      const response = await rankingAPI.getClaimsHistory(claimsFilter);
      setClaimsHistory(response.data);
      setSelectedClaims([]); // Limpa seleção
    } catch (error) {
      console.error('Erro ao carregar histórico de resgates:', error);
    }
  };

  const handleDeleteSelectedClaims = async () => {
    console.log('🗑️ Clicou deletar, selecionados:', selectedClaims);
    
    if (selectedClaims.length === 0) {
      toast.error('Selecione pelo menos um resgate');
      return;
    }

    // Abre dialog de confirmação ao invés de window.confirm
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      console.log('📤 Enviando para API:', selectedClaims);
      const response = await rankingAPI.deleteClaimsBulk(selectedClaims);
      console.log('✅ Resposta da API:', response.data);
      toast.success(`${selectedClaims.length} resgate(s) excluído(s)!`);
      setShowDeleteConfirm(false);
      loadClaimsHistory();
    } catch (error) {
      console.error('❌ Erro ao excluir:', error);
      console.error('Detalhes:', error.response?.data);
      toast.error('Erro ao excluir resgates');
      setShowDeleteConfirm(false);
    }
  };

  const toggleClaimSelection = (claimId) => {
    console.log('🔲 Toggle checkbox:', claimId);
    setSelectedClaims(prev => {
      const newSelection = prev.includes(claimId) 
        ? prev.filter(id => id !== claimId)
        : [...prev, claimId];
      console.log('📋 Selecionados agora:', newSelection);
      return newSelection;
    });
  };

  const toggleSelectAll = (dayClaims) => {
    const dayIds = dayClaims.map(c => c.id);
    const allSelected = dayIds.every(id => selectedClaims.includes(id));
    
    if (allSelected) {
      setSelectedClaims(prev => prev.filter(id => !dayIds.includes(id)));
    } else {
      setSelectedClaims(prev => [...new Set([...prev, ...dayIds])]);
    }
  };

  const handleApplyWinnersFilter = async () => {
    try {
      const response = await rankingAPI.getWinners(100);  // Busca todos
      let filtered = response.data;
      
      if (winnersFilter.start_date) {
        filtered = filtered.filter(w => w.date >= winnersFilter.start_date);
      }
      if (winnersFilter.end_date) {
        filtered = filtered.filter(w => w.date <= winnersFilter.end_date);
      }
      
      setWinners(filtered);
    } catch (error) {
      toast.error('Erro ao filtrar ganhadores');
    }
  };


  const handleRemoveWinner = async (kakoId, date) => {
    const formattedDate = date.split('-').reverse().join('/');
    
    if (!window.confirm(`Tem certeza que deseja remover este ganhador do histórico?\n\nUsuário: ${kakoId}\nData: ${formattedDate}\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await rankingAPI.removeUser(kakoId, date);
      toast.success('Ganhador removido do histórico!');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover ganhador');
    }
  };


  const handleUpdateConfig = async () => {
    try {
      await rankingAPI.updateConfig({ 
        prizes,
        top_positions: 10  // Sempre 10 para cobrir todos prêmios
      });
      toast.success('Prêmios atualizados!');
      loadData();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast.error('Erro ao atualizar configuração');
    }
  };

  const handleAdjustPoints = async () => {
    if (!adjustingUser || !pointsAdjustment) return;
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${BACKEND_URL}/api/ranking/adjust-points`, null, {
        params: {
          kako_id: adjustingUser.kako_id,
          date: adjustingUser.date,
          points_adjustment: parseInt(pointsAdjustment)
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pontos ajustados: ${parseInt(pointsAdjustment) > 0 ? '+' : ''}${pointsAdjustment}`);
      setAdjustDialog(false);
      setPointsAdjustment('');
      loadData();
    } catch (error) {
      toast.error('Erro ao ajustar pontos');
    }
  };

  const handleUpdatePrize = (index, field, value) => {
    const newPrizes = [...prizes];
    newPrizes[index] = { ...newPrizes[index], [field]: value };
    setPrizes(newPrizes);
  };

  const handleRemoveUser = (kakoId, date) => {
    setRemovingUser({ kakoId, date });
    setShowRemoveUserConfirm(true);
  };

  const confirmRemoveUser = async () => {
    try {
      await rankingAPI.removeUser(removingUser.kakoId, removingUser.date);
      toast.success('Usuário removido do ranking!');
      setShowRemoveUserConfirm(false);
      setRemovingUser(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao remover usuário');
      setShowRemoveUserConfirm(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ranking-management">
      <div>
        <h2 className="text-2xl font-bold">Ranking Diário</h2>
        <p className="text-gray-600">Gerencie o ranking e prêmios dos compradores</p>
      </div>

      <Tabs defaultValue="current" className="w-full">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="current">
              <Trophy className="w-4 h-4 mr-2" />
              Ranking de Hoje
            </TabsTrigger>
            <TabsTrigger value="prizes">
              <Gift className="w-4 h-4 mr-2" />
              Configurar Prêmios
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-2" />
              Ganhadores
            </TabsTrigger>
            <TabsTrigger value="claims">
              <Gift className="w-4 h-4 mr-2" />
              Resgates
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="current" className="mt-6">
          <Card data-testid="current-ranking-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Ranking de Hoje
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {currentRanking.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">Nenhum dado hoje</p>
                ) : (
                  currentRanking.map((user, idx) => (
                    <div key={user.kako_id} className="border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {idx === 0 && '🥇'}
                            {idx === 1 && '🥈'}
                            {idx === 2 && '🥉'}
                            {idx > 2 && <span className="font-bold">{idx + 1}º</span>}
                          </span>
                          {user.kako_avatar && (
                            <img
                              src={user.kako_avatar}
                              alt={user.kako_nickname}
                              className="w-10 h-10 rounded-full border-2 border-white shadow"
                            />
                          )}
                          <div>
                            <p className="font-medium">{user.kako_nickname}</p>
                            <p className="text-xs text-gray-600">KAKO ID: {user.kako_id}</p>
                            <p className="text-xs text-gray-600">{user.order_count} pedidos</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="font-bold text-purple-600 text-xl">{user.total_coins.toLocaleString()} 🪙</p>
                            <p className="text-xs text-gray-600">moedas</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAdjustingUser(user);
                              setAdjustDialog(true);
                            }}
                            className="text-xs"
                          >
                            ⚙️ Ajustar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveUser(user.kako_id, user.date)}
                            data-testid={`remove-user-${user.kako_id}`}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="prizes" className="mt-6">
          <Card data-testid="prizes-config-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Configurar Prêmios Diários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">Configure os prêmios para os Top 10 do dia</p>
              
              {/* Tabela de Prêmios */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="text-center p-3 font-semibold">Posição</th>
                      <th className="text-left p-3 font-semibold">Descrição</th>
                      <th className="text-center p-3 font-semibold">Tipo</th>
                      <th className="text-center p-3 font-semibold">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prizes.map((prize, idx) => (
                      <tr key={idx} className={`border-b ${idx < 3 ? 'bg-yellow-50' : ''}`}>
                        {/* Posição */}
                        <td className="p-3 text-center font-bold">
                          {idx + 1}º
                          {idx === 0 && ' 🥇'}
                          {idx === 1 && ' 🥈'}
                          {idx === 2 && ' 🥉'}
                        </td>
                        
                        {/* Descrição */}
                        <td className="p-3">
                          <Input
                            placeholder="Descrição do prêmio"
                            value={prize.description}
                            onChange={(e) => handleUpdatePrize(idx, 'description', e.target.value)}
                            className="text-sm"
                          />
                        </td>
                        
                        {/* Tipo */}
                        <td className="p-3">
                          <select
                            className="w-full border rounded px-3 py-2 text-sm"
                            value={prize.type || 'fixed'}
                            onChange={(e) => handleUpdatePrize(idx, 'type', e.target.value)}
                          >
                            <option value="fixed">💰 Fixo</option>
                            <option value="percentage">📊 %</option>
                          </select>
                        </td>
                        
                        {/* Valor */}
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              placeholder={prize.type === 'percentage' ? '0.5' : '10000'}
                              value={prize.value !== undefined ? prize.value : (prize.coins || 0)}
                              onChange={(e) => {
                                const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                handleUpdatePrize(idx, 'value', val);
                              }}
                              className="text-sm w-32 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <span className="text-xs text-gray-600 whitespace-nowrap">
                              {prize.type === 'percentage' ? '%' : '🪙'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <Button onClick={handleUpdateConfig} className="w-full bg-yellow-600 hover:bg-yellow-700">
                Salvar Prêmios
              </Button>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                <p className="font-bold">ℹ️ Como funciona:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Os prêmios são atribuídos automaticamente à meia-noite</li>
                  <li>Apenas os top 10 do dia recebem prêmios</li>
                  <li>Ganhadores têm 24h para resgatar</li>
                  <li>Se não resgatar em 24h, perde o prêmio</li>
                  <li>Defina 0 moedas para não dar prêmio em uma posição</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card data-testid="winners-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="h-5 w-5 text-yellow-500" />
                Histórico de Ganhadores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtros */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm">Data Início</Label>
                  <Input
                    type="date"
                    value={winnersFilter.start_date}
                    onChange={(e) => setWinnersFilter({ ...winnersFilter, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Fim</Label>
                  <Input
                    type="date"
                    value={winnersFilter.end_date}
                    onChange={(e) => setWinnersFilter({ ...winnersFilter, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">&nbsp;</Label>
                  <Button onClick={handleApplyWinnersFilter} className="w-full bg-purple-600 hover:bg-purple-700">
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              {/* Lista de Ganhadores Agrupados por Data */}
              <div className="space-y-4">
                {winners.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">Nenhum ganhador ainda</p>
                ) : (
                  // Agrupa por data
                  Object.entries(
                    winners.reduce((groups, winner) => {
                      const date = winner.date;
                      if (!groups[date]) groups[date] = [];
                      groups[date].push(winner);
                      return groups;
                    }, {})
                  ).map(([date, dayWinners]) => (
                    <div key={date} className="border rounded-lg p-3 bg-gray-50">
                      <h4 className="font-bold text-sm mb-3 text-purple-600">
                        📅 {date.split('-').reverse().join('/')}
                      </h4>
                      <div className="space-y-2">
                        {dayWinners.map((winner) => (
                          <div key={winner.id} className="flex items-center justify-between p-2 bg-white rounded">
                            <div className="flex items-center gap-3">
                              <span className="text-lg">
                                {winner.position === 1 && '🥇'}
                                {winner.position === 2 && '🥈'}
                                {winner.position === 3 && '🥉'}
                                {winner.position > 3 && `${winner.position}º`}
                              </span>
                              {winner.kako_avatar && (
                                <img
                                  src={winner.kako_avatar}
                                  alt={winner.kako_nickname}
                                  className="w-8 h-8 rounded-full border-2 border-white shadow"
                                />
                              )}
                              <div>
                                <p className="font-medium text-sm">{winner.kako_nickname}</p>
                                <p className="text-xs text-gray-600">ID: {winner.kako_id}</p>
                                <p className="text-xs text-gray-600">{winner.total_coins.toLocaleString()} 🪙</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <Badge className={`text-xs ${winner.prize_claimed ? 'bg-green-600' : 'bg-red-600'}`}>
                                  {winner.prize_claimed ? '✓' : '✗'}
                                </Badge>
                                {winner.prize_coins && (
                                  <p className="text-xs text-gray-600 mt-1">+{winner.prize_coins.toLocaleString()} 🎁</p>
                                )}
                              </div>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleRemoveWinner(winner.kako_id, winner.date)}
                                data-testid={`remove-winner-${winner.kako_id}-${winner.date}`}
                                className="text-xs h-7"
                              >
                                Remover
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="claims" className="mt-6">
          <Card data-testid="claims-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-green-600" />
                Histórico de Resgates de Prêmios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtros e Ordenação — modelo Tarefas VIP */}
              <div className="grid gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label className="text-sm">KAKO ID</Label>
                  <Input
                    placeholder="Filtrar por usuário"
                    value={claimsFilter.kako_id}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, kako_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Início</Label>
                  <Input
                    type="date"
                    value={claimsFilter.start_date}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Fim</Label>
                  <Input
                    type="date"
                    value={claimsFilter.end_date}
                    onChange={(e) => setClaimsFilter({ ...claimsFilter, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Ordenar por</Label>
                  <select
                    value={claimsSortBy}
                    onChange={(e) => setClaimsSortBy(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="date_desc">Data (Recente)</option>
                    <option value="date_asc">Data (Antigo)</option>
                    <option value="claimed_desc">Resgate (Recente)</option>
                    <option value="claimed_asc">Resgate (Antigo)</option>
                    <option value="coins_desc">Moedas (Maior)</option>
                    <option value="coins_asc">Moedas (Menor)</option>
                    <option value="position_asc">Posição (1º → último)</option>
                    <option value="position_desc">Posição (último → 1º)</option>
                    <option value="kako_id_asc">KAKO ID (A-Z)</option>
                    <option value="kako_id_desc">KAKO ID (Z-A)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">&nbsp;</Label>
                  <Button onClick={loadClaimsHistory} className="w-full bg-green-600 hover:bg-green-700">
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              {/* Botão de Delete Selecionados */}
              {selectedClaims.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded">
                  <span className="text-sm font-medium">
                    {selectedClaims.length} resgate(s) selecionado(s)
                  </span>
                  <Button
                    onClick={handleDeleteSelectedClaims}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir Selecionados
                  </Button>
                </div>
              )}

              {/* Lista de Resgates Agrupados por Data — modelo das Tarefas VIP */}
              <div className="space-y-3">
                {claimsHistory.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhum resgate realizado ainda</p>
                ) : (
                  (() => {
                    const sortedHistory = [...claimsHistory].sort((a, b) => {
                      switch (claimsSortBy) {
                        case 'date_desc':    return (b.date || '').localeCompare(a.date || '');
                        case 'date_asc':     return (a.date || '').localeCompare(b.date || '');
                        case 'claimed_desc': return new Date(b.prize_claimed_at || 0) - new Date(a.prize_claimed_at || 0);
                        case 'claimed_asc':  return new Date(a.prize_claimed_at || 0) - new Date(b.prize_claimed_at || 0);
                        case 'coins_desc':   return (b.prize_coins || 0) - (a.prize_coins || 0);
                        case 'coins_asc':    return (a.prize_coins || 0) - (b.prize_coins || 0);
                        case 'position_asc': return (a.position || 99) - (b.position || 99);
                        case 'position_desc':return (b.position || 0) - (a.position || 0);
                        case 'kako_id_asc':  return (a.kako_id || '').localeCompare(b.kako_id || '');
                        case 'kako_id_desc': return (b.kako_id || '').localeCompare(a.kako_id || '');
                        default: return 0;
                      }
                    });

                    return Object.entries(
                      sortedHistory.reduce((groups, claim) => {
                        const date = claim.date;
                        if (!groups[date]) groups[date] = [];
                        groups[date].push(claim);
                        return groups;
                      }, {})
                    ).map(([date, dayClaims]) => (
                      <div key={date} className="border rounded-lg p-3 bg-green-50 mb-3">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={dayClaims.every(c => selectedClaims.includes(c.id))}
                              onCheckedChange={() => toggleSelectAll(dayClaims)}
                            />
                            <h4 className="font-bold text-sm text-green-700">
                              📅 {date.split('-').reverse().join('/')}
                            </h4>
                          </div>
                          <Badge className="bg-green-600 text-white">
                            {dayClaims.length} resgate(s) • Total: {dayClaims.reduce((s, c) => s + (c.prize_coins || 0), 0).toLocaleString()} 🪙
                          </Badge>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="p-2 w-10">
                                  <Checkbox
                                    checked={dayClaims.every(c => selectedClaims.includes(c.id))}
                                    onCheckedChange={(checked) => {
                                      dayClaims.forEach(c => {
                                        if (checked && !selectedClaims.includes(c.id)) {
                                          setSelectedClaims(s => [...s, c.id]);
                                        } else if (!checked) {
                                          setSelectedClaims(s => s.filter(id => id !== c.id));
                                        }
                                      });
                                    }}
                                  />
                                </th>
                                <th className="text-left p-2">Posição</th>
                                <th className="text-left p-2">Usuário</th>
                                <th className="text-left p-2">Prêmio</th>
                                <th className="text-left p-2">Descrição</th>
                                <th className="text-left p-2">Resgatado em</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dayClaims.map((claim) => (
                                <tr key={claim.id} className="border-b hover:bg-white">
                                  <td className="p-2">
                                    <Checkbox
                                      checked={selectedClaims.includes(claim.id)}
                                      onCheckedChange={() => toggleClaimSelection(claim.id)}
                                    />
                                  </td>
                                  <td className="p-2">
                                    <span className="text-lg">
                                      {claim.position === 1 && '🥇'}
                                      {claim.position === 2 && '🥈'}
                                      {claim.position === 3 && '🥉'}
                                      {claim.position > 3 && `${claim.position}º`}
                                    </span>
                                  </td>
                                  <td className="p-2">
                                    <div className="flex items-center gap-2">
                                      {claim.kako_avatar && (
                                        <img src={claim.kako_avatar} alt="" className="w-7 h-7 rounded-full border" />
                                      )}
                                      <div className="text-xs">
                                        <div className="font-medium">{claim.kako_nickname}</div>
                                        <div className="text-gray-500">ID: {claim.kako_id}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-2">
                                    <Badge className="bg-green-600 text-white text-xs">
                                      +{(claim.prize_coins || 0).toLocaleString()} 🎁
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-xs text-gray-600">{claim.prize_description || '-'}</td>
                                  <td className="p-2 text-xs">
                                    {claim.prize_claimed_at ? new Date(claim.prize_claimed_at).toLocaleString('pt-BR') : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ));
                  })()
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Dialog Ajustar Pontos */}
      <Dialog open={adjustDialog} onOpenChange={setAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar Pontos no Ranking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Usuário: <span className="font-bold">{adjustingUser?.kako_nickname}</span></p>
              <p className="text-xs text-gray-500">Total atual: {adjustingUser?.total_coins.toLocaleString()} 🪙</p>
            </div>
            <div>
              <Label>Ajuste de Pontos</Label>
              <Input
                type="number"
                placeholder="+100 ou -50"
                value={pointsAdjustment}
                onChange={(e) => setPointsAdjustment(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Use números positivos para adicionar ou negativos para remover
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialog(false)}>Cancelar</Button>
            <Button onClick={handleAdjustPoints} className="bg-purple-600">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir <strong>{selectedClaims.length} resgate(s)</strong> selecionado(s)?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação irá reverter os resgates. As moedas NÃO serão devolvidas aos usuários.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Remover Usuário do Ranking */}
      <Dialog open={showRemoveUserConfirm} onOpenChange={setShowRemoveUserConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover Usuário do Ranking</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja remover este usuário do ranking?
          </p>
          {removingUser && (
            <div className="text-xs bg-gray-50 p-2 rounded mt-2">
              <p>KAKO ID: {removingUser.kakoId}</p>
              <p>Data: {removingUser.date?.split('-').reverse().join('/')}</p>
            </div>
          )}
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveUserConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmRemoveUser}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Remoção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RankingManagement;
